import { FC, PropsWithChildren, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import {
	Dialog,
	DialogContent,
	DialogTitle,
	DialogDescription,
	DialogHeader,
	DialogFooter,
	DialogClose,
} from '@/components/ui/dialog';
import ETextInput from '@/components/everridge/ETextInput';
import { z } from 'zod';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import Cancel_01_Icon from '@/components/icons/Cancel_01_Icon';

type EmailDto = {
	email: string;
};

const EmailFormSchema = z.object({
	email: z
		.string()
		.min(1, 'Email is needed.')
		.regex(/^[\w-\.]+@([\w-]+\.)+[\w-]{2,4}$/gi, 'Wrong email format.'),
});

type EmailFormData = z.infer<typeof EmailFormSchema>;

type Props = {
	email?: string;
	onClose: () => void;
};

const EChangeEmailModal: FC<PropsWithChildren<Props>> = ({
	email,
	onClose,
}) => {
	const [isOpen, setIsOpen] = useState(true);
	const { t } = useTranslation();

	const { control, handleSubmit } = useForm<EmailFormData>({
		resolver: zodResolver(EmailFormSchema),
		defaultValues: {
			email: email || '',
		},
	});

	const handleClose = () => {
		setIsOpen(false);
		setTimeout(onClose, 300);
	};

	const submitFormHandler = async (form: EmailFormData) => {
		const dto: EmailDto = {
			email: form.email,
		};
		onClose();
	};

	return (
		<Dialog
			open={isOpen}
			onOpenChange={handleClose}>
			<DialogContent>
				<DialogHeader>
					<DialogTitle>
						{t('Change email')}{' '}
						<DialogClose className='ml-auto cursor-pointer'>
							<Cancel_01_Icon />
						</DialogClose>
					</DialogTitle>
				</DialogHeader>
				<div className='flex flex-col gap-4'>
					<DialogDescription>
						Enter a new email address. 4-digit verification code
						will be sent to confirm your email.
					</DialogDescription>
					<ETextInput
						control={control}
						fieldName={'email'}
						label={t('New email')}
						placeholder='your@email.com'
					/>
				</div>
				<DialogFooter className='justify-end'>
					<Button
						variant='outline'
						onClick={onClose}>
						{t('Cancel')}
					</Button>
					<Button onClick={handleSubmit(submitFormHandler)}>
						{t('Send code')}
					</Button>
				</DialogFooter>
			</DialogContent>
		</Dialog>
	);
};

export default EChangeEmailModal;
